/*
 * Fields added to a play on play save based on other fields in the same play.
 * These fields are not editable by the user.
 */

import _ from "lodash";
import { getTeamSideFromId } from "games/utils";

const playsWithUnchangedBallSpot = ["incompletePass", "endOfQuarter"];

/*
 * Sets the ball spot values for plays missing the ball spot fields
 * because we know the ball spot is the same as the yard line
 */
const getBallSpot = (play) => {
  const { playType } = play;
  return playsWithUnchangedBallSpot.includes(playType) ? play.yardLine : null;
};
const getBallSpotTeamId = (play) => {
  const { playType } = play;
  return playsWithUnchangedBallSpot.includes(playType)
    ? play.yardLineTeamId
    : null;
};

/*
 * Sets the distance to go value only if it's "Goal".
 * Otherwise it is not set here, but instead set via the field in the form.
 */
const getDistanceToGo = (play) => {
  return play.isGoalDistanceToGo ? 100 : null;
};

/*
 * The number of points gained for a specific side in a play
 * @param {object} play - most recently edited / added play
 * @param {"home"|"away"} side
 * @param {object} game
 * @returns {number}
 */
const getPointsGained = (play, side, game) => {
  const {
    playType = null,
    teamInPossessionId = null,
    includesTouchdown = false,
    includesScore = false
  } = play;

  const offenseSide = getTeamSideFromId(game, teamInPossessionId);
  const isOffense = offenseSide === side;

  // defense scoring
  if (!isOffense && playType === "safety") {
    return 2;
  } else if (!isOffense) {
    return 0;
  }

  const twoPtPlays = [
    "PATBlockedKickReturn",
    "PATFumbleReturn",
    "PATInterceptionReturn",
    "PATPass",
    "PATRun"
  ];

  if (includesTouchdown) {
    return 6;
  } else if (playType === "fieldGoal" && includesScore) {
    return 3;
  } else if (twoPtPlays.includes(playType) && includesScore) {
    return 2;
  } else if (playType === "PATKick" && includesScore) {
    return 1;
  } else {
    return 0;
  }
};

/*
 * @param {object} play - most recent state of play from form
 * @param {object} previousPlay - play that is 1 before (in order, not time) current play
 * @param {object} game - game from current state
 */
const postPlayChanges = (play, previousPlay, game) => {
  const homePointsGained = getPointsGained(play, "home", game);
  const awayPointsGained = getPointsGained(play, "away", game);
  const previousHomeTotalPoints = _.get(previousPlay, "homeTotalPoints", 0);
  const previousAwayTotalPoints = _.get(previousPlay, "awayTotalPoints", 0);
  const homeTotalPoints = previousHomeTotalPoints + homePointsGained;
  const awayTotalPoints = previousAwayTotalPoints + awayPointsGained;
  const ballSpot = getBallSpot(play);
  const ballSpotTeamId = getBallSpotTeamId(play);
  const distanceToGo = getDistanceToGo(play);

  return {
    homeTotalPoints,
    awayTotalPoints,
    homePointsGained,
    awayPointsGained,
    ...(ballSpot && { ballSpot }),
    ...(ballSpotTeamId && { ballSpotTeamId }),
    ...(distanceToGo && { distanceToGo })
  };
};

export { postPlayChanges, getPointsGained };
